import React, { useState, useRef, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, X, Library, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

// Drag-and-drop image picker. Stores a URL (uploaded /api/public/media/... or external
// https URL) + alt text. Images only; type + size validated client and server side.
export const MediaPicker = ({
  label = "Image",
  value,
  alt,
  onUrlChange,
  onAltChange,
  testid = "media",
  help,
}) => {
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [libOpen, setLibOpen] = useState(false);
  const [lib, setLib] = useState([]);
  const [libLoading, setLibLoading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const inputRef = useRef(null);

  const doUpload = useCallback(
    async (file) => {
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        toast.error("Please choose an image (JPG, PNG, WEBP, or GIF).");
        return;
      }
      if (file.size > 8 * 1024 * 1024) {
        toast.error("Image is too large (max 8 MB).");
        return;
      }
      setUploading(true);
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("alt", alt || "");
        const { data } = await api.post("/site/media/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onUrlChange(data.url);
        toast.success("Image uploaded.");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    },
    [alt, onUrlChange]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const loadLib = useCallback(async () => {
    setLibLoading(true);
    try {
      const { data } = await api.get("/site/media");
      setLib(data.items || []);
    } catch {
      setLib([]);
    } finally {
      setLibLoading(false);
    }
  }, []);

  useEffect(() => {
    if (libOpen) loadLib();
  }, [libOpen, loadLib]);

  return (
    <div className="space-y-2" data-testid={testid}>
      {label ? <Label className="text-[13px] font-semibold text-[#1C4358]">{label}</Label> : null}

      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt={alt || "Selected media preview"}
            data-testid={`${testid}-preview`}
            className="h-32 w-auto max-w-full rounded-lg border border-[#1C4358]/15 object-cover bg-[#F1F5F8]"
          />
          <button
            type="button"
            data-testid={`${testid}-remove`}
            onClick={() => onUrlChange("")}
            aria-label="Remove image"
            className="absolute -top-2 -right-2 h-7 w-7 inline-flex items-center justify-center rounded-full bg-[#9C1E22] text-white shadow hover:bg-[#861A1D]"
          >
            <X size={15} />
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          data-testid={`${testid}-dropzone`}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-7 text-center transition-colors cursor-pointer ${
            drag ? "border-[#1C4358] bg-[#1C4358]/6" : "border-[#1C4358]/25 hover:border-[#1C4358]/50 bg-[#F7FAFC]"
          }`}
        >
          {uploading ? (
            <Loader2 className="animate-spin text-[#1C4358]" size={22} />
          ) : (
            <UploadCloud className="text-[#1C4358]" size={22} />
          )}
          <p className="text-[13px] text-[#4B5563]">
            <span className="font-semibold text-[#1C4358]">Drag &amp; drop</span> an image, or click to browse
          </p>
          <p className="text-[11px] text-[#6B7280]">JPG, PNG, WEBP, GIF · max 8 MB</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        data-testid={`${testid}-input`}
        onChange={(e) => doUpload(e.target.files?.[0])}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" data-testid={`${testid}-browse`}
          onClick={() => inputRef.current?.click()} disabled={uploading} className="gap-1.5 h-8 text-[13px]">
          <UploadCloud size={14} /> Upload
        </Button>
        <Button type="button" size="sm" variant="outline" data-testid={`${testid}-library`}
          onClick={() => setLibOpen(true)} className="gap-1.5 h-8 text-[13px]">
          <Library size={14} /> Library
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Link2 size={14} className="text-[#6B7280] shrink-0" />
        <Input
          data-testid={`${testid}-url`}
          value={/^https?:\/\//i.test(value || "") ? value : urlInput}
          placeholder="…or paste an image URL (https://…)"
          className="h-8 text-[13px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0"
          onChange={(e) => {
            setUrlInput(e.target.value);
            onUrlChange(e.target.value.trim());
          }}
        />
      </div>

      <Input
        data-testid={`${testid}-alt`}
        value={alt || ""}
        placeholder="Alt text (describe the image for accessibility & SEO)"
        className="h-8 text-[13px] rounded-lg border-[#1C4358]/20 focus-visible:ring-0"
        onChange={(e) => onAltChange && onAltChange(e.target.value)}
      />
      {help ? <p className="text-[11px] text-[#6B7280]">{help}</p> : null}

      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="max-w-2xl" data-testid={`${testid}-library-modal`}>
          <DialogHeader>
            <DialogTitle>Media library</DialogTitle>
            <DialogDescription>Pick a previously uploaded image.</DialogDescription>
          </DialogHeader>
          {libLoading ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="animate-spin text-[#1C4358]" size={24} />
            </div>
          ) : lib.length === 0 ? (
            <p className="py-10 text-center text-[14px] text-[#6B7280]">No uploads yet. Upload an image to start your library.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto py-1">
              {lib.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`${testid}-lib-item`}
                  onClick={() => {
                    onUrlChange(m.url);
                    if (onAltChange && m.alt) onAltChange(m.alt);
                    setLibOpen(false);
                  }}
                  className="group relative rounded-lg overflow-hidden border border-[#1C4358]/15 hover:border-[#1C4358] focus-visible:border-[#1C4358] transition-colors"
                >
                  <img src={m.url} alt={m.alt || m.original_name || "Library image"} className="h-24 w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MediaPicker;
